import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { publicAPI } from '../services/api';
import {
  Watch,
  MapPin,
  Building2,
  Globe,
  Instagram,
  Linkedin,
  ExternalLink,
} from 'lucide-react';
import ImageCarousel from '../components/ImageCarousel';

const PublicDealerProfilePage = () => {
  const { dealerId } = useParams();
  const [profile, setProfile] = useState(null);
  const [listings, setListings] = useState({ watches: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetch = async () => {
      try {
        const [profileRes, listingsRes] = await Promise.all([
          publicAPI.getDealerProfile(dealerId),
          publicAPI.getDealerListings(dealerId, { limit: 24 }),
        ]);
        setProfile(profileRes.data);
        setListings(listingsRes.data);
      } catch (e) {
        setError(e.response?.status === 404 ? 'Dealer not found' : e.message || 'Failed to load');
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [dealerId]);

  const formatPrice = (price) => {
    if (price == null) return '';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(price);
  };

  const locationParts = [profile?.city, profile?.state, profile?.country].filter(Boolean);
  const location = locationParts.length ? locationParts.join(', ') : null;

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-gray-50 to-stone-100 flex items-center justify-center">
        <div className="animate-spin rounded-full h-16 w-16 border-4 border-slate-900 border-t-transparent" />
      </div>
    );
  }

  if (error || !profile) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-gray-50 to-stone-100 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-3xl shadow-xl p-8 text-center">
          <div className="w-20 h-20 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Building2 className="w-10 h-10 text-amber-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Dealer Not Found</h2>
          <p className="text-gray-600 mb-4">
            This dealer profile is not available or does not exist.
          </p>
          <Link
            to="/"
            className="inline-block px-6 py-3 rounded-lg bg-slate-900 text-white font-medium hover:bg-slate-800"
          >
            Go home
          </Link>
        </div>
      </div>
    );
  }

  const instagramUrl = profile.instagram_handle
    ? `https://instagram.com/${profile.instagram_handle.replace(/^@/, '')}`
    : null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-gray-50 to-stone-100 py-12 px-4">
      <div className="max-w-5xl mx-auto">
        {/* Header / Hero */}
        <div className="bg-white rounded-3xl shadow-xl border border-gray-100 overflow-hidden mb-8">
          <div className="p-8 md:p-10">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
              <div className="flex items-start gap-4">
                <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-slate-700 to-slate-900 flex items-center justify-center flex-shrink-0">
                  <Building2 className="w-10 h-10 text-white" />
                </div>
                <div>
                  <h1 className="text-2xl md:text-3xl font-bold text-gray-900">
                    {profile.dealer_name}
                  </h1>
                  {profile.company_name && (
                    <p className="text-gray-600 mt-1 flex items-center gap-2">
                      <Building2 className="w-4 h-4" />
                      {profile.company_name}
                    </p>
                  )}
                  {location && (
                    <p className="text-gray-500 text-sm mt-1 flex items-center gap-2">
                      <MapPin className="w-4 h-4" />
                      {location}
                    </p>
                  )}
                </div>
              </div>
              {/* Social / links */}
              <div className="flex flex-wrap items-center gap-3">
                {profile.website_url && (
                  <a
                    href={profile.website_url.startsWith('http') ? profile.website_url : `https://${profile.website_url}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-slate-100 text-slate-800 font-medium hover:bg-slate-200 transition-colors"
                  >
                    <Globe className="w-4 h-4" />
                    Website
                    <ExternalLink className="w-3.5 h-3.5" />
                  </a>
                )}
                {instagramUrl && (
                  <a
                    href={instagramUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-pink-100 text-pink-800 font-medium hover:bg-pink-200 transition-colors"
                  >
                    <Instagram className="w-4 h-4" />
                    {profile.instagram_handle}
                    <ExternalLink className="w-3.5 h-3.5" />
                  </a>
                )}
                {profile.linkedin_url && (
                  <a
                    href={profile.linkedin_url.startsWith('http') ? profile.linkedin_url : `https://${profile.linkedin_url}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-100 text-blue-800 font-medium hover:bg-blue-200 transition-colors"
                  >
                    <Linkedin className="w-4 h-4" />
                    LinkedIn
                    <ExternalLink className="w-3.5 h-3.5" />
                  </a>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Listings */}
        <div>
          <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
            <Watch className="w-6 h-6" />
            Public listings
            {listings.total > 0 && (
              <span className="text-sm font-normal text-gray-500">({listings.total})</span>
            )}
          </h2>
          {listings.watches.length === 0 ? (
            <div className="bg-white rounded-2xl border border-gray-100 p-12 text-center text-gray-500">
              No public listings at the moment.
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {listings.watches.map((w) => (
                <Link
                  key={w.id}
                  to={`/watch/${w.id}`}
                  className="bg-white rounded-2xl border border-gray-100 overflow-hidden shadow-sm hover:shadow-md transition-shadow"
                >
                  <div className="aspect-square bg-gray-100 relative">
                    {w.images?.length > 0 ? (
                      <ImageCarousel images={w.images} />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Watch className="w-16 h-16 text-gray-300" />
                      </div>
                    )}
                  </div>
                  <div className="p-4">
                    <p className="font-semibold text-gray-900">
                      {w.brand} {w.model}
                    </p>
                    {w.reference && (
                      <p className="text-sm text-gray-500">{w.reference}</p>
                    )}
                    {w.asking_price != null && (
                      <p className="text-lg font-bold text-gray-900 mt-1">
                        {formatPrice(w.asking_price)}
                      </p>
                    )}
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PublicDealerProfilePage;
